import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { ShieldCheck, ShieldAlert, RefreshCw, Loader2, ChevronDown, ChevronUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PERIODS = ["today", "week", "month", "year"];

const money = (v) => `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Pill = ({ pass }) =>
  pass === null ? (
    <span className="text-[10px] uppercase tracking-wide text-white/35 border border-white/15 rounded-full px-2 py-0.5">N/A</span>
  ) : pass ? (
    <span className="text-[10px] uppercase tracking-wide text-emerald-400 border border-emerald-400/30 bg-emerald-400/10 rounded-full px-2 py-0.5 flex items-center gap-1"><ShieldCheck size={11} /> Match</span>
  ) : (
    <span className="text-[10px] uppercase tracking-wide text-red-400 border border-red-400/30 bg-red-400/10 rounded-full px-2 py-0.5 flex items-center gap-1"><ShieldAlert size={11} /> Mismatch</span>
  );

export const ReconciliationAuditCard = ({ token }) => {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showDefs, setShowDefs] = useState(false);

  const load = useCallback(async (p) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/finance/reconciliation-audit`, {
        params: { period: p }, headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load the reconciliation audit.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(period); }, [period, load]);

  const pass = data?.overall_status === "PASS";

  return (
    <div data-testid="reconciliation-audit-card" className="rounded-2xl border border-white/10 bg-[#0C0C0C] p-5 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display font-bold text-white flex items-center gap-2">
            <ShieldCheck size={18} className="text-[#F2B705]" /> Cross-Screen Revenue Reconciliation
          </h3>
          <p className="text-white/45 text-sm mt-0.5">Read-only. Independently proves Command Center, Financials, and the Money Dashboard agree to the penny.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-[#0A0A0A] border border-white/10 rounded-lg p-1">
            {PERIODS.map((p) => (
              <button key={p} data-testid={`recon-period-${p}`} onClick={() => setPeriod(p)}
                className={`text-xs font-semibold px-3 py-1.5 rounded-md capitalize transition-colors ${period === p ? "bg-[#F2B705] text-black" : "text-white/50 hover:text-white/80"}`}>
                {p}
              </button>
            ))}
          </div>
          <button data-testid="recon-refresh" onClick={() => load(period)} disabled={loading}
            className="text-white/50 hover:text-[#F2B705] border border-white/10 rounded-lg p-2 transition-colors disabled:opacity-50">
            {loading ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
          </button>
        </div>
      </div>

      {error && <div data-testid="recon-error" className="text-sm text-red-400 border border-red-400/30 bg-red-400/[0.06] rounded-lg px-3 py-2">{error}</div>}

      {data && !error && (
        <>
          <div data-testid="recon-overall-status"
            className={`rounded-xl border px-4 py-3 flex items-center justify-between ${pass ? "border-emerald-400/30 bg-emerald-400/[0.06]" : "border-red-400/30 bg-red-400/[0.06]"}`}>
            <div className="flex items-center gap-2">
              {pass ? <ShieldCheck size={18} className="text-emerald-400" /> : <ShieldAlert size={18} className="text-red-400" />}
              <span className={`font-display font-black text-lg ${pass ? "text-emerald-400" : "text-red-400"}`}>{data.overall_status}</span>
              <span className="text-white/45 text-xs">{data.range?.start} → {data.range?.end} (America/Chicago)</span>
            </div>
            <div className="text-right">
              <div className="text-white/40 text-[11px] uppercase tracking-wide">Canonical Collected</div>
              <div data-testid="recon-canonical-value" className="font-display font-black text-xl text-gold-gradient">{money(data.canonical_collected)}</div>
            </div>
          </div>

          <div className="rounded-xl border border-white/10 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-widest text-white/40 border-b border-white/10">
                  <th className="px-4 py-2.5">Screen</th>
                  <th className="px-4 py-2.5">Endpoint</th>
                  <th className="px-4 py-2.5 text-right">Value</th>
                  <th className="px-4 py-2.5 text-right">Diff</th>
                  <th className="px-4 py-2.5 text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {(data.screens || []).map((sc) => (
                  <tr key={sc.screen} data-testid={`recon-row-${sc.screen.replace(/\s+/g, "-").toLowerCase()}`} className="border-b border-white/5">
                    <td className="px-4 py-2.5 text-white/85 font-semibold">{sc.screen}</td>
                    <td className="px-4 py-2.5 text-white/35 text-xs font-mono">{sc.endpoint}</td>
                    <td className="px-4 py-2.5 text-right text-white/80">{sc.value === null ? "—" : money(sc.value)}</td>
                    <td className={`px-4 py-2.5 text-right ${sc.match === false ? "text-red-400 font-bold" : "text-white/40"}`}>
                      {sc.diff_from_canonical !== undefined ? money(sc.diff_from_canonical) : "—"}
                    </td>
                    <td className="px-4 py-2.5 text-center"><Pill pass={sc.match} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.screens?.some((s) => s.note) && (
              <div className="px-4 py-2.5 text-[11px] text-white/35 border-t border-white/5 space-y-1">
                {data.screens.filter((s) => s.note).map((s) => <div key={s.screen}>· <span className="text-white/50">{s.screen}:</span> {s.note}</div>)}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div data-testid="recon-included" className="bg-[#0A0A0A] border border-white/10 rounded-xl p-3">
              <div className="text-white/40 text-[11px] uppercase tracking-wide">Included ({data.source_records.included_collected.count})</div>
              <div className="text-white/85 font-bold mt-1">{money(data.source_records.included_collected.total)}</div>
            </div>
            <div data-testid="recon-refunds" className="bg-[#0A0A0A] border border-white/10 rounded-xl p-3">
              <div className="text-white/40 text-[11px] uppercase tracking-wide">Refunds Subtracted ({data.source_records.refunds_subtracted.count})</div>
              <div className="text-white/85 font-bold mt-1">{money(data.source_records.refunds_subtracted.total)}</div>
            </div>
            <div data-testid="recon-excluded-reversed" className="bg-[#0A0A0A] border border-white/10 rounded-xl p-3">
              <div className="text-white/40 text-[11px] uppercase tracking-wide">Excluded — Reversed ({data.source_records.excluded_reversed.count})</div>
              <div className="text-white/85 font-bold mt-1">{money(data.source_records.excluded_reversed.total)}</div>
            </div>
            <div data-testid="recon-excluded-duplicate" className="bg-[#0A0A0A] border border-white/10 rounded-xl p-3">
              <div className="text-white/40 text-[11px] uppercase tracking-wide">Excluded — Possible Dup ({data.source_records.excluded_duplicate.count})</div>
              <div className="text-white/85 font-bold mt-1">{money(data.source_records.excluded_duplicate.total)}</div>
            </div>
          </div>

          <div data-testid="recon-alert-integrity"
            className={`rounded-xl border px-4 py-3 flex items-center justify-between ${data.alert_integrity.match ? "border-white/10" : "border-red-400/30 bg-red-400/[0.06]"}`}>
            <div>
              <div className="text-white/70 text-sm font-semibold">Command Center Alert Integrity</div>
              <div className="text-white/40 text-xs mt-0.5">{data.alert_integrity.definition}</div>
            </div>
            <div className="text-right shrink-0 ml-4">
              <div className="text-white/85 text-sm">{data.alert_integrity.command_center_money_alerts} alerts vs {data.alert_integrity.canonical_active_balance_count} active-balance</div>
              <Pill pass={data.alert_integrity.match} />
            </div>
          </div>

          <button data-testid="recon-defs-toggle" onClick={() => setShowDefs((v) => !v)}
            className="text-xs text-white/45 hover:text-[#F2B705] flex items-center gap-1 transition-colors">
            Metric definitions {showDefs ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
          {showDefs && (
            <div data-testid="recon-definitions" className="text-xs text-white/50 space-y-1.5 border border-white/10 rounded-lg p-3">
              {Object.entries(data.definitions || {}).map(([k, v]) => (
                <div key={k}><span className="text-white/75 font-semibold capitalize">{k.replace(/_/g, " ")}:</span> {v}</div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};
